import React, { useState, useEffect } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import { getProfile } from '../lib/metadata';
import { getAllCommunities, createCommunity, joinCommunity, leaveCommunity, isMember, getCommunityMembers } from '../lib/communities';
import { CommunityDetail } from './CommunityDetail';
import { publishEvent } from '../lib/sync';
import { COLORS, SPACING } from '../styles/theme';

export function Communities({ onBack, onViewUserProfile, onViewVideo }: { onBack: () => void; onViewUserProfile: (userId: string) => void; onViewVideo: (hash: string) => void }) {
  const [communities, setCommunities] = useState<any[]>([]);
  const [identity, setIdentity] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [selectedCommunityId, setSelectedCommunityId] = useState<string | null>(null);

  useEffect(() => {
    const id = loadOrCreateIdentity();
    setIdentity(id);

    const prof = getProfile();
    setProfile(prof);

    loadCommunities();
  }, []);

  const loadCommunities = () => {
    const all = getAllCommunities();
    setCommunities(all);
  };

  const handleCreate = async () => {
    if (!newName.trim() || !identity || !profile) return;

    const community = createCommunity(newName.trim(), newDesc.trim(), identity.userId, profile.username);
    await publishEvent('community', community);
    await publishEvent('community_member', {
      communityId: community.id,
      userId: identity.userId,
      username: profile.username,
      joinedAt: Date.now()
    });
    setNewName('');
    setNewDesc('');
    setShowCreate(false);
    loadCommunities();
  };

  const handleJoin = async (communityId: string) => {
    if (!identity || !profile) return;
    joinCommunity(communityId, identity.userId, profile.username);
    await publishEvent('community_member', {
      communityId,
      userId: identity.userId,
      username: profile.username,
      joinedAt: Date.now()
    });
    loadCommunities();
  };

  const handleLeave = (communityId: string) => {
    if (!identity) return;
    leaveCommunity(communityId, identity.userId);
    loadCommunities();
  };

  if (selectedCommunityId) {
    return (
      <CommunityDetail
        communityId={selectedCommunityId}
        onBack={() => {
          setSelectedCommunityId(null);
          loadCommunities();
        }}
        onViewUserProfile={onViewUserProfile}
        onViewVideo={onViewVideo}
      />
    );
  }

  return (
    <div style={{
      border: `2px outset ${COLORS.highlight}`,
      backgroundColor: COLORS.panel,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{
        backgroundColor: COLORS.accent,
        color: '#fff',
        padding: `${SPACING.xs} ${SPACING.sm}`,
        fontSize: '9px',
        fontWeight: 700,
        borderBottom: `2px solid ${COLORS.highlight}`,
        userSelect: 'none'
      }}>
        Communities
      </div>

      <div style={{
        padding: SPACING.lg,
        backgroundColor: COLORS.bg,
        borderBottom: `1px solid ${COLORS.border}`
      }}>
        <button
          onClick={() => setShowCreate(!showCreate)}
          style={{
            padding: `${SPACING.sm} ${SPACING.lg}`,
            backgroundColor: COLORS.panel,
            color: COLORS.accent,
            border: `2px outset ${COLORS.highlight}`,
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontSize: '9px',
            fontWeight: 700,
            textDecoration: 'underline',
            marginBottom: showCreate ? SPACING.lg : 0,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)'
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.borderStyle = 'inset';
            e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.borderStyle = 'outset';
            e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
          }}
        >
          + Create
        </button>

        {showCreate && (
          <div style={{
            padding: SPACING.lg,
            backgroundColor: COLORS.bg,
            border: `2px inset ${COLORS.highlight}`,
            marginTop: SPACING.lg
          }}>
            <input
              type="text"
              placeholder="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={50}
              style={{
                display: 'block',
                width: '100%',
                padding: SPACING.md,
                marginBottom: SPACING.md,
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: 'monospace',
                fontSize: '10px',
                boxSizing: 'border-box'
              }}
            />
            <textarea
              placeholder="Description"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              maxLength={200}
              style={{
                display: 'block',
                width: '100%',
                padding: SPACING.md,
                minHeight: '60px',
                marginBottom: SPACING.md,
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: 'monospace',
                fontSize: '10px',
                boxSizing: 'border-box'
              }}
            />
            <button
              onClick={handleCreate}
              disabled={!newName.trim()}
              style={{
                padding: `${SPACING.sm} ${SPACING.lg}`,
                backgroundColor: newName.trim() ? COLORS.accent : COLORS.panel,
                color: newName.trim() ? '#fff' : COLORS.textMuted,
                border: `2px outset ${newName.trim() ? COLORS.accent : COLORS.border}`,
                cursor: newName.trim() ? 'pointer' : 'default',
                fontFamily: 'monospace',
                fontSize: '9px',
                fontWeight: 700,
                textDecoration: 'underline',
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)'
              }}
              onMouseDown={(e) => {
                if (newName.trim()) {
                  e.currentTarget.style.borderStyle = 'inset';
                  e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
                }
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.borderStyle = 'outset';
                e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
              }}
            >
              Create
            </button>
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: COLORS.bg }}>
        {communities.length === 0 ? (
          <p style={{ padding: SPACING.lg, color: COLORS.textMuted, margin: 0, fontSize: '10px' }}>
            No communities
          </p>
        ) : (
          communities.map((community, idx) => {
            const isMemberBool = identity && isMember(community.id, identity.userId);
            const members = getCommunityMembers(community.id);

            return (
              <div
                key={community.id}
                onClick={() => setSelectedCommunityId(community.id)}
                style={{
                  padding: SPACING.lg,
                  borderBottom: `1px solid ${COLORS.border}`,
                  cursor: 'pointer',
                  backgroundColor: idx % 2 === 0 ? COLORS.bg : 'rgba(139, 30, 45, 0.05)',
                  transition: 'all 0.1s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(139, 30, 45, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = idx % 2 === 0 ? COLORS.bg : 'rgba(139, 30, 45, 0.05)';
                }}
              >
                <h4 style={{
                  margin: `0 0 ${SPACING.sm} 0`,
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  fontStyle: 'italic',
                  color: COLORS.accent
                }}>
                  {community.name}
                </h4>
                <p style={{
                  margin: `0 0 ${SPACING.sm} 0`,
                  fontSize: '8px',
                  color: COLORS.textMuted,
                  fontWeight: 700
                }}>
                  By{' '}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewUserProfile(community.creatorId);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      color: COLORS.accent,
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: 'inherit',
                      fontFamily: 'inherit'
                    }}
                  >
                    {community.creatorUsername}
                  </button>
                  {' '} | {members.length} members
                </p>
                <p style={{
                  margin: `0 0 ${SPACING.md} 0`,
                  color: COLORS.text,
                  fontSize: '10px'
                }}>
                  {community.description}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    isMemberBool ? handleLeave(community.id) : handleJoin(community.id);
                  }}
                  style={{
                    padding: `${SPACING.xs} ${SPACING.lg}`,
                    backgroundColor: isMemberBool ? 'rgba(139, 30, 45, 0.3)' : COLORS.accent,
                    color: isMemberBool ? COLORS.accent : '#fff',
                    border: `2px outset ${isMemberBool ? COLORS.accent : COLORS.accent}`,
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: '8px',
                    fontWeight: 700,
                    textDecoration: 'underline',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)'
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.borderStyle = 'inset';
                    e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.borderStyle = 'outset';
                    e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
                  }}
                >
                  {isMemberBool ? 'Leave' : 'Join'}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
